import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { PAGE_SCROLL_CONTAINER_SELECTOR } from '../injection-tokens';
import { BootstrapTheme } from '../utils';

/**
 * Spinner animation for some works
 */
@Component({
  selector: 'mbs-loader',
  templateUrl: './loader.component.html',
  host: {
    '(window:resize)': 'onResize()'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoaderComponent implements AfterViewInit {
  private readonly pageScrollContainerSelector = inject(PAGE_SCROLL_CONTAINER_SELECTOR);
  private readonly documentHasVerticalScrollbar = signal<boolean>(false);

  /**
   * Spinner size.
   * * 'xsmall' - 35px * 35px
   * * 'small' - 75px * 75px
   * * 'default' - 150px * 150px
   * @param {'small' | 'default' | 'xsmall'} value
   */
  public readonly hasVerticalScroll = input<boolean>(false);
  public readonly size = input<'small' | 'default' | 'xsmall'>('default');
  public readonly text = input<string | null>(null);
  public readonly theme = input<BootstrapTheme>(null);
  public readonly type = input<BootstrapTheme>(null);
  public readonly useFixedDocumentSpinner = input<boolean>(false);
  public readonly useFixedSize = input<boolean>(false);

  protected readonly fixedSpinnerClass = computed<string>(() =>
    this.useFixedDocumentSpinner() && this.documentHasVerticalScrollbar() ? 'mbs-loader_spinner_fixed' : ''
  );

  protected readonly spinnerColorClass = computed<string>(() => {
    const theme = this.theme();

    return theme ? `mbs-loader_spinner-${theme}` : '';
  });

  protected readonly spinnerSizeClass = computed<string>(() => {
    switch (this.size()) {
      case 'small':
        return 'mbs-loader_spinner-small';
      case 'xsmall':
        return 'mbs-loader_spinner-xsmall';
      case 'default':
      default:
        return '';
    }
  });

  protected readonly spinnerClasses = computed<string>(() =>
    [this.fixedSpinnerClass(), this.spinnerColorClass(), this.spinnerSizeClass()].filter(Boolean).join(' ')
  );

  protected readonly bgClass = computed<string>(() => {
    if (this.text()) return '-cover';

    const type = this.type();

    return type ? `bg-transparent-${type}` : '';
  });

  protected readonly useFixedSizeEffect = effect(() => this.checkSpinnerPosition());

  onResize(): void {
    this.checkSpinnerPosition();
  }

  ngAfterViewInit(): void {
    this.checkSpinnerPosition();
  }

  private checkSpinnerPosition(): void {
    if (!this.useFixedDocumentSpinner()) return;

    const pageScrollContainer = this.pageScrollContainerSelector ? document.querySelector(this.pageScrollContainerSelector) : null;
    const isPageScrollable =
      document.documentElement.scrollHeight > document.documentElement.clientHeight ||
      (!!pageScrollContainer && pageScrollContainer.scrollHeight > pageScrollContainer.clientHeight);
    const hasScrollbar = this.useFixedSize() ? this.hasVerticalScroll() : isPageScrollable;

    this.documentHasVerticalScrollbar.set(hasScrollbar);
  }
}
